import "../Tasks/tasks.css"
import "./Securitytasks.css"
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";


/* ── Progress Ring ── */
const ProgressRing = ({ completed, required }) => {
    const size = 52;
    const stroke = 4.5;
    const radius = (size - stroke) / 2;
    const circ = 2 * Math.PI * radius;
    const pct = Math.min(completed / Math.max(required, 1), 1);
    const done = pct >= 1;

    return (
        <div className="progress-ring-wrap">
            <svg className="progress-ring-svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle className="progress-ring-bg" cx={size/2} cy={size/2} r={radius} strokeWidth={stroke} />
                <circle
                    className={`progress-ring-fill sec-ring-fill ${done ? "progress-ring-done sec-ring-done" : ""}`}
                    cx={size/2} cy={size/2} r={radius}
                    strokeWidth={stroke}
                    strokeDasharray={circ}
                    strokeDashoffset={circ * (1 - pct)}
                />
                {done && (
                    <circle className="progress-ring-pulse sec-ring-pulse" cx={size/2} cy={size/2} r={radius} strokeWidth={stroke} />
                )}
            </svg>
            <span className={`progress-ring-text sec-ring-text ${done ? "progress-ring-text-done sec-ring-text-done" : ""}`}>
                {completed}
            </span>
        </div>
    );
};


/* ── Countdown Timer ── */
const Timer = ({ type }) => {
    const [timeLeft, setTimeLeft] = useState("");

    useEffect(() => {
        const calc = () => {
            const now = new Date();
            let target;
            if (type === "daily") {
                target = new Date(now);
                target.setHours(24, 0, 0, 0);
            } else {
                target = new Date(now);
                const day = target.getDay();
                const daysUntilMon = day === 0 ? 1 : 8 - day;
                target.setDate(target.getDate() + daysUntilMon);
                target.setHours(0, 0, 0, 0);
            }
            const diff = target - now;
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setTimeLeft(`${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`);
        };
        calc();
        const id = setInterval(calc, 1000);
        return () => clearInterval(id);
    }, [type]);

    return (
        <div className="timer sec-timer">
            <span className="timer-label">{type === "daily" ? "Resets in" : "Week ends in"}</span>
            <span className="timer-countdown sec-timer-countdown">{timeLeft}</span>
        </div>
    );
};


/* ── Floating +1 / -1 ── */
const FloatingNumber = ({ value, id, onDone }) => {
    useEffect(() => {
        const t = setTimeout(() => onDone(id), 800);
        return () => clearTimeout(t);
    }, [id, onDone]);

    return (
        <span className={`float-number ${value > 0 ? "sec-float-positive" : "float-negative"}`}>
            {value > 0 ? `+${value}` : value}
        </span>
    );
};


/* ── Section Header ── */
const SectionHeader = ({ title, icon, children }) => (
    <div className="section-header">
        <div className="section-header-left">
            <span className="section-icon">{icon}</span>
            <h2 className="section-title sec-section-title">{title}</h2>
        </div>
        {children}
    </div>
);


/* ── Task Card ── */
const TaskCard = ({ task, onUpdate }) => {
    const [floats, setFloats] = useState([]);
    const pct = Math.min(task.completed / Math.max(task.required, 1), 1);
    const done = pct >= 1;

    const handleClick = (dir) => {
        const val = dir === "+1" ? 1 : -1;
        if (task.completed + val < 0) return;
        setFloats(f => [...f, { value: val, id: Date.now() }]);
        onUpdate(dir, task);
    };

    const removeFloat = (id) => setFloats(f => f.filter(x => x.id !== id));

    return (
        <div className={`card sec-card ${done ? "card-done sec-card-done" : ""}`}>
            <div className="card-top">
                <div className="card-info">
                    <h1 className="card-name sec-card-name">
                        {task.sort_key}
                        {done && <span className="card-check sec-card-check">✓</span>}
                    </h1>
                    <div className="card-rewards">
                        {task.reward1 && <span className="reward reward1">{task.reward1}</span>}
                        {task.reward2 && <span className="reward reward2">{task.reward2}</span>}
                        {task.reward3 && <span className="reward reward3">{task.reward3}</span>}
                        {task.reward4 && <span className="reward reward4">{task.reward4}</span>}
                    </div>
                </div>
                <ProgressRing completed={task.completed} required={task.required} />
            </div>

            <div className="progress-bar-track">
                <div
                    className={`progress-bar-fill sec-bar-fill ${done ? "progress-bar-done sec-bar-done" : ""}`}
                    style={{ width: `${pct * 100}%` }}
                />
            </div>

            <div className="card-buttons">
                {floats.map(f => (
                    <FloatingNumber key={f.id} value={f.value} id={f.id} onDone={removeFloat} />
                ))}
                <button className="counter-button counter-minus" onClick={() => handleClick("-1")}>−</button>
                <button className="counter-button sec-counter-plus" onClick={() => handleClick("+1")}>+</button>
            </div>
        </div>
    );
};


/* ── Main Security Tasks Page ── */
const SecurityTasks = () => {
    const [dailyTasks, setDailyTasks] = useState([]);
    const [weeklyTasks, setWeeklyTasks] = useState([]);
    const [streak, setStreak] = useState(0);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();
    const BASE = "https://thj20r4htl.execute-api.us-east-1.amazonaws.com/prod/tasks";

    useEffect(() => {
        Promise.all([getDaily(), getWeekly(), getStreak()]).then(() => setLoading(false));
    }, []);

    const getDaily = async () => {
        try {
            const r = await fetch(`${BASE}?param=sec-daily`, { headers: { "content-type": "application/json" } });
            setDailyTasks(await r.json());
        } catch (e) { console.error(e); }
    };
    const getWeekly = async () => {
        try {
            const r = await fetch(`${BASE}?param=sec-weekly`, { headers: { "content-type": "application/json" } });
            setWeeklyTasks(await r.json());
        } catch (e) { console.error(e); }
    };
    const getStreak = async () => {
        try {
            const r = await fetch(`${BASE}?param=sec-streak`, { headers: { "content-type": "application/json" } });
            const data = await r.json();
            setStreak(data[0]?.value || 0);
        } catch (e) { console.error(e); }
    };

    const updateCount = async (direction, item) => {
        const delta = direction === "+1" ? 1 : -1;
        const newCompleted = item.completed + delta;
        if (newCompleted < 0) return;

        const update = (list, setList) =>
            setList(list.map(t => t.sort_key === item.sort_key ? { ...t, completed: newCompleted } : t));
        update(dailyTasks, setDailyTasks);
        update(weeklyTasks, setWeeklyTasks);

        try {
            await fetch(BASE, {
                method: "PUT",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ ...item, completed: newCompleted }),
            });
        } catch (e) {
            console.error(e);
            getDaily();
            getWeekly();
        }
    };

    const dailyDone = dailyTasks.filter(t => t.completed >= t.required).length;
    const weeklyDone = weeklyTasks.filter(t => t.completed >= t.required).length;

    return (
        <div className="page sec-page">
            {/* Nav */}
            <div className="header sec-header">
                <button className="nav-button" onClick={() => navigate("/")}>⚡ Tasks</button>
                <button className="nav-button" onClick={() => navigate("/shop")}>🛒 Shop</button>
                <button className="nav-button sec-nav-active" onClick={() => navigate("/security")}>🛡️ Security</button>
                <button className="nav-button" onClick={() => navigate("/X")}>✦ X</button>
            </div>

            <div className="content">
                {/* Streak */}
                <div className="streak sec-streak">
                    <span className="streak-fire">🛡️</span>
                    <div className="streak-inner">
                        <h1 className="streak-count sec-streak-count">{streak}</h1>
                        <span className="streak-label sec-streak-label">day streak</span>
                    </div>
                </div>

                {/* Quick stats */}
                <div className="quick-stats">
                    <div className="stat">
                        <span className="stat-number sec-stat-daily">{dailyDone}/{dailyTasks.length}</span>
                        <span className="stat-label">daily</span>
                    </div>
                    <div className="stat-divider sec-stat-divider" />
                    <div className="stat">
                        <span className="stat-number sec-stat-weekly">{weeklyDone}/{weeklyTasks.length}</span>
                        <span className="stat-label">weekly</span>
                    </div>
                </div>

                {/* Daily */}
                <section className="section">
                    <SectionHeader title="Daily Ops" icon="🔒">
                        <Timer type="daily" />
                    </SectionHeader>
                    <div className="daily">
                        {dailyTasks.map(task => (
                            <TaskCard key={task.sort_key} task={task} onUpdate={updateCount} />
                        ))}
                    </div>
                    {dailyTasks.length === 0 && !loading && (
                        <p className="empty-state">No daily tasks found</p>
                    )}
                </section>

                {/* Weekly */}
                <section className="section">
                    <SectionHeader title="Weekly Missions" icon="🎯">
                        <Timer type="weekly" />
                    </SectionHeader>
                    <div className="weekly">
                        {weeklyTasks.map(task => (
                            <TaskCard key={task.sort_key} task={task} onUpdate={updateCount} />
                        ))}
                    </div>
                    {weeklyTasks.length === 0 && !loading && (
                        <p className="empty-state">No weekly tasks found</p>
                    )}
                </section>
            </div>
        </div>
    );
};

export default SecurityTasks;